'use client'

import React, { useState, useId, FC, ReactNode } from 'react'
import { ArrowDown } from '../../icons/arrow-down'
import { cn } from 'design-system'

interface AccordionItemProps {
  title: {
    text: string
    icons?: ReactNode
  }
  children: ReactNode
  isOpen: boolean
  onToggle: () => void
  type?: 'white' | 'gray'
  headingLevel?: 'h2' | 'h3' | 'h4'
}

export interface AccordionProps {
  items: Omit<AccordionItemProps, 'isOpen' | 'onToggle' | 'headingLevel'>[]
  className?: string
  collapse?: boolean
  type?: 'white' | 'gray'
  headingLevel?: 'h2' | 'h3' | 'h4'
}

const backgroundColors = {
  white: 'bg-white',
  gray: 'bg-[#f9fafb]'
}

const AccordionItem: FC<AccordionItemProps> = ({
  title,
  children,
  isOpen,
  onToggle,
  type = 'gray',
  headingLevel = 'h3'
}) => {
  const id = useId()
  const headerId = `accordion-header-${id}`
  const panelId = `accordion-panel-${id}`
  const HeadingLevel = headingLevel

  return (
    <div className={cn('mb-2 rounded-xl shadow-sm', backgroundColors[type])}>
      <HeadingLevel className="text-base">
        <button
          id={headerId}
          onClick={onToggle}
          className="relative flex w-full items-center justify-between rounded-xl bg-transparent px-4 py-3 text-left transition-colors duration-300 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-300"
          aria-expanded={isOpen}
          aria-controls={panelId}
          type="button">
          <span className="font-normal">{title.text}</span>
          <div className="flex items-center gap-4">
            {!isOpen && title?.icons}
            <ArrowDown
              size={18}
              aria-hidden="true"
              className={cn('shrink-0 text-blue-600 transition-transform', {
                '-rotate-180': isOpen
              })}
            />
          </div>
        </button>
      </HeadingLevel>
      {isOpen ? (
        <div
          id={panelId}
          role="region"
          aria-labelledby={headerId}
          className="px-4 py-3 [&_div]:text-gray-600 [&_p]:text-gray-600 [&_span]:text-gray-600">
          {children}
        </div>
      ) : null}
    </div>
  )
}

export const Accordion: FC<AccordionProps> = ({
  items,
  className,
  collapse = false,
  type = 'gray',
  headingLevel = 'h3'
}) => {
  const [openIndices, setOpenIndices] = useState<number[]>(() => [0])

  const handleToggle = (index: number) => {
    setOpenIndices(prev => {
      if (prev.includes(index)) {
        return collapse ? prev.filter(i => i !== index) : []
      }
      return collapse ? [...prev, index] : [index]
    })
  }

  return (
    <div className={className} role="presentation">
      {items.map((item, idx) => (
        <AccordionItem
          {...item}
          key={`accordion-item-${idx}`}
          isOpen={openIndices.includes(idx)}
          onToggle={() => handleToggle(idx)}
          type={type}
          headingLevel={headingLevel}
        />
      ))}
    </div>
  )
}
